import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

export type StackProps<T extends keyof React.JSX.IntrinsicElements = 'div'> = {
  /**
   * The element to render as.
   * @default 'div'
   */
  as?: T;
  /**
   * Content to be stacked vertically.
   */
  children: React.ReactNode;
  /**
   * Additional className for the container. Merged with the `.stack` base class
   * via cn(). Apply a gap modifier here — e.g. `className="stack-sm"`.
   */
  className?: string;
} & Omit<React.ComponentPropsWithoutRef<T>, 'children' | 'className'>;

/**
 * Stack — vertical layout primitive for arranging items in a column.
 *
 * All visual layout is applied via CSS classes. This wrapper provides:
 * - The `.stack` base class applied automatically (vertical flex column, 20px gap)
 * - Polymorphism via `as` (default `'div'`), rendered with `React.createElement`
 * - `data-component="Stack"` for component identification (consumer can override)
 * - `className` merging via `cn()`
 * - Full HTML attribute passthrough (id, aria-label, role, etc.)
 *
 * Gap is controlled entirely through the CSS class API — the base `.stack` carries
 * the default 20px gap, and a flat `.stack-{none|thin|xs|sm|default}` modifier
 * (applied via `className`) overrides it. There is no `gap` prop.
 *
 * Stack is inherently vertical — use Inline for horizontal layouts. There is no
 * ref forwarding (the polymorphic `React.createElement` form does not forward a ref).
 *
 * @example
 * ```tsx
 * // Default 20px gap
 * <Stack>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Stack>
 *
 * // Tighter gap via the modifier class
 * <Stack className="stack-sm">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Stack>
 *
 * // Render as a different element with native attributes
 * <Stack as="section" className="stack-thin" aria-label="Content section">
 *   <h2>Title</h2>
 *   <p>Content</p>
 * </Stack>
 *
 * // className merges with the `.stack` base class (does not replace)
 * <Stack className="stack-sm p-4 border">
 *   <div>Padded, bordered stack</div>
 * </Stack>
 *
 * // Scroll container recipe (nested flex child)
 * <Stack className="stack-none min-h-0 overflow-y-auto">
 *   <div>...</div>
 * </Stack>
 *
 * // Override data-component for feature-scoped semantics
 * <Stack data-component="SidebarStack" data-testid="sidebar-stack">
 *   <div>...</div>
 * </Stack>
 * ```
 */
export const Stack = <T extends keyof React.JSX.IntrinsicElements = 'div'>({
  as,
  className,
  children,
  ...props
}: StackProps<T>) => {
  const Component = as || 'div';

  return React.createElement(
    Component,
    {
      'data-component': 'Stack',
      className: cn('stack', className),
      ...props,
    },
    children
  );
};

Stack.displayName = 'Stack';
